import { AbstractHttpClient, AbstractStorage, AbstractTokenManager } from "@clairejs/client";
import { getSystemLocale } from "@clairejs/core";
import { useDispatch, useStore } from "@clairejs/react";
import { Outlet, useNavigator } from "@clairejs/react-web";
import { APIProvider } from "@vis.gl/react-google-maps";
import { App, ConfigProvider, Modal, theme } from "antd";
import { initializeApp } from "firebase/app";
import { useEffect } from "react";

import { config } from "../config/config";

import { ApiClient } from "../utils/api-client";

import { GOOGLE_MAP_API_KEY, LANG_STORAGE_KEY, langs } from "../helpers/constants";

import { sessionActions } from "../store/action/session";
import { userActions } from "../store/action/user";
import { DataStore } from "../store/store";

import { useColorMode } from "../store/hooks/ui";

import { useInject, useSetLanguage } from "../hooks/system";

import CustomSuspense from "../components/custom-suspense";
import OtpInput from "../components/otp-input";

const useLoadLanguages = () => {
    const navigator = useNavigator();
    const storage = useInject(AbstractStorage);

    const { lang, setLanguage } = useSetLanguage();

    const loadLanguage = async () => {
        const queryLang = navigator.getQueries().lang;

        const lang = await storage.getItem<string>(LANG_STORAGE_KEY).catch(() => undefined);

        const userLang = queryLang || lang;
        setLanguage(userLang && langs[userLang] ? userLang : getSystemLocale());
    };

    useEffect(() => {
        if (!lang) {
            loadLanguage();
        }
    }, [lang]);
};

const useSetupColorMode = () => {
    const { token } = theme.useToken();
    const { mode, loadMode } = useColorMode();

    useEffect(() => {
        if (mode) {
            document.body.style.backgroundColor = token.colorBgBase;
            document.body.style.color = token.colorTextBase;
        }
    }, [token, mode]);

    useEffect(() => {
        loadMode();
    }, []);
};

function RootApp() {
    useLoadLanguages();
    useSetupColorMode();

    const navigator = useNavigator();
    const tokenManager = useInject(AbstractTokenManager);
    const httpClient = useInject(AbstractHttpClient) as ApiClient;

    const me = useStore(DataStore, (s) => s.me);

    const dispatch = useDispatch(DataStore);
    const dispatchUser = useDispatch(DataStore, (s) => s.me);

    useEffect(() => {
        //-- set up session expired handle
        httpClient.sessionExpiredCallback = () => {
            Modal.confirm({
                title: "Phiên làm việc hết hạn",
                content: "Bạn vui lòng đăng nhập lại",
                onOk: async () => {
                    await dispatch(sessionActions.logout());
                    navigator.navigate("/");
                },
            });
        };

        httpClient.tfaRequiredCallback = () => {
            let code = "";

            return new Promise<void>((resolve) => {
                Modal.confirm({
                    width: 600,
                    title: "Yêu cầu TFA",
                    content: (
                        <div>
                            <div style={{ textAlign: "center", marginBottom: 5 }}>
                                {"Vui lòng nhập TFA để xác thực"}
                            </div>
                            <OtpInput
                                style={{ margin: 21 }}
                                otpLength={6}
                                onChange={(otp) => {
                                    code = otp;
                                }}
                            />
                        </div>
                    ),
                    onCancel: () => resolve(),
                    onOk: async () => {
                        //-- upgrade tfa token using otp
                        try {
                            await dispatch(sessionActions.tfaUpgrade(code));
                        } finally {
                            resolve();
                        }
                    },
                });
            });
        };
    }, [dispatch, navigator]);

    useEffect(() => {
        if (me?.hasToken) {
            //-- fetch user info
            dispatchUser(userActions.fetchCurrentUser());
        }
    }, [me?.hasToken]);

    useEffect(() => {
        tokenManager.getAccessToken().then((token) => dispatchUser({ hasToken: !!token }));
    }, []);

    useEffect(() => {
        config.FIREBASE_APP && initializeApp(config.FIREBASE_APP);
    }, []);

    useEffect(() => {
        dispatch(sessionActions.fetchUserInfoFromLocal());
    }, []);

    return (
        <APIProvider apiKey={GOOGLE_MAP_API_KEY}>
            <CustomSuspense>
                <Outlet />
            </CustomSuspense>
        </APIProvider>
    );
}

export default function Root() {
    const mode = useStore(DataStore, (s) => s.ui?.color?.explicitMode);

    return (
        <ConfigProvider theme={{ algorithm: mode === "dark" ? theme.darkAlgorithm : theme.defaultAlgorithm }}>
            <App style={{ flex: 1, display: "flex" }}>
                <RootApp />
            </App>
        </ConfigProvider>
    );
}
